// Pure gate evaluation for the dock section registry.
//
// Split out of `sectionRegistry.tsx` in RP-2a of
// docs/planning/firefly_role_context_presentation.md §10 — the file
// was crossing the 300-line soft hygiene cap with the capabilities
// extension and the gate-evaluation logic was the natural lift-out:
// it's pure, has no dependency on the production `DOCK_SECTIONS`
// constant, and is the future home for any further gate-axis
// additions (e.g. `currentBucket` lookups in RP-3+).
//
// Types still live in `sectionRegistry.tsx` so callers continue to
// import `DockSectionDefinition` / `DockSectionRoleGate` / `HasRoleFn`
// from one canonical place. This file only re-exports the pure
// helpers; tests + the production hook update their import path.

import {
  NO_CAPABILITIES,
  type HasCapabilityFn,
} from "@/lib/capabilities/capabilities";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";
import type {
  DockSectionDefinition,
  DockSectionRoleGate,
  HasRoleFn,
} from "./sectionRegistry";
import { DOCK_SECTIONS } from "./sectionRegistry";

/**
 * Pure filter: given an arbitrary list of sections, a role-check fn,
 * and an optional capability-check fn, returns only the sections
 * whose `roleGate` is satisfied. Exposed separately from the default
 * registry so tests can pass synthetic sections without coupling to
 * the live `DOCK_SECTIONS` array.
 *
 * `hasCapability` defaults to `NO_CAPABILITIES` (always returns
 * false) so any section that opts into a capability gate before its
 * caller has been wired up to a real capability source fails closed.
 * This is the same fail-closed posture the `{ roles: [] }` rule
 * already enforces.
 */
export function filterDockSectionsByRoles(
  sections: readonly DockSectionDefinition[],
  hasRole: HasRoleFn,
  hasCapability: HasCapabilityFn = NO_CAPABILITIES,
): readonly DockSectionDefinition[] {
  return sections.filter((s) =>
    sectionMatchesGate(s.roleGate, hasRole, hasCapability),
  );
}

/**
 * Pure filter: given an arbitrary list of sections + the active
 * bucket, returns only the sections whose `bucketRelevance` is
 * compatible with `currentBucket`. Composes orthogonally to
 * `filterDockSectionsByRoles` — `bucketRelevance` and `roleGate`
 * live on different axes of `DockSectionDefinition`, so the two
 * filters are kept separate and combined at the orchestrator
 * (`getDockSectionsForAuth`) rather than fused into one predicate.
 *
 * Match rules (L2 of
 * docs/planning/firefly_role_context_presentation.md, promotes the
 * RP-1 `bucketRelevance` tag into a hard render filter):
 *
 *   - `bucketRelevance === undefined` ⇒ match (back-compat with the
 *     RP-1 default: a section without a bucket tag is relevant in
 *     every bucket).
 *   - `bucketRelevance` is a non-empty list and `currentBucket` is
 *     in it ⇒ match.
 *   - `bucketRelevance` is a non-empty list and `currentBucket` is
 *     NOT in it ⇒ no match (the cleanup this slice ships — composite-
 *     role users no longer see sales-shaped sections while operating
 *     in manager/platform/mfg buckets).
 *   - `bucketRelevance` is a non-empty list and `currentBucket` is
 *     `null` ⇒ **match (fail-open)**. Production never hits this —
 *     `LeadWorkspaceShell` always threads `currentMode` to the dock,
 *     so by the time the panel renders `currentBucket` is set. The
 *     null branch is purely defensive for test mounts and any
 *     hypothetical early-bootstrap frame; failing open keeps the
 *     dock from flashing emptier-than-it-should-be states. See
 *     §L2 in the planning doc for the full rationale.
 *
 * `bucketRelevance: []` is forbidden by the lock test in
 * `tests/dockSectionsRegistryTags.test.ts`; nothing in production
 * declares it. Defensively, an empty list here also fails-open by
 * the second rule above (length-0 list never contains the bucket),
 * which matches the RP-1 row that said "empty-array semantics
 * deferred; use omit instead."
 */
export function filterDockSectionsByBucket(
  sections: readonly DockSectionDefinition[],
  currentBucket: BucketId | null,
): readonly DockSectionDefinition[] {
  return sections.filter((s) =>
    sectionMatchesBucket(s.bucketRelevance, currentBucket),
  );
}

function sectionMatchesBucket(
  bucketRelevance: readonly BucketId[] | undefined,
  currentBucket: BucketId | null,
): boolean {
  if (bucketRelevance === undefined) return true;
  if (currentBucket === null) return true;
  return bucketRelevance.includes(currentBucket);
}

/**
 * Production lookup. Used by `useActivityDockSections`.
 *
 * Composes both axes of the section filter: role/capability gate
 * (RP-2a + RP-3) first, bucket filter (L2) second. `currentBucket`
 * defaults to `null` (fail-open per `filterDockSectionsByBucket`)
 * so pre-L2 callers and tests that don't pass it keep seeing the
 * full role-filtered set.
 */
export function getDockSectionsForAuth(
  hasRole: HasRoleFn,
  hasCapability: HasCapabilityFn = NO_CAPABILITIES,
  currentBucket: BucketId | null = null,
): readonly DockSectionDefinition[] {
  const roleFiltered = filterDockSectionsByRoles(
    DOCK_SECTIONS,
    hasRole,
    hasCapability,
  );
  return filterDockSectionsByBucket(roleFiltered, currentBucket);
}

/**
 * Pure gate evaluator. `roles` and `capabilities` are **unioned**:
 * a section is visible iff at least one declared field produces a
 * match. A gate with neither field declared (`{}`) fails closed; an
 * empty list on a declared field contributes no matches (so
 * `{ roles: [] }` alone still fails closed, matching the pre-RP-2a
 * fail-closed posture). See `DockSectionRoleGate` JSDoc for the
 * full semantics and the RP-3 rationale for choosing union over
 * intersection.
 */
function sectionMatchesGate(
  gate: DockSectionRoleGate,
  hasRole: HasRoleFn,
  hasCapability: HasCapabilityFn,
): boolean {
  if (gate === "any-dock-audience") return true;
  const { roles, capabilities } = gate;
  if (roles === undefined && capabilities === undefined) return false;
  if (roles !== undefined && roles.length > 0 && hasRole(...roles)) {
    return true;
  }
  if (
    capabilities !== undefined &&
    capabilities.length > 0 &&
    hasCapability(...capabilities)
  ) {
    return true;
  }
  return false;
}
